import { ArrowLeft, Clock, RefreshCw, Shield } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** The full-page wait of the guard, while the session or the rights are read. */
export function FullPageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen" role="status" aria-label="Chargement">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0066FF]" />
    </div>
  );
}

interface CardProps {
  readonly tone: 'amber' | 'red';
  readonly icon: ReactNode;
  readonly title: string;
  readonly children: ReactNode;
}

function Card({ tone, icon, title, children }: CardProps) {
  const ring = tone === 'amber' ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600';
  return (
    <div className="flex items-center justify-center min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-lg p-8 text-center">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${ring}`}>{icon}</div>
          <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

function SignOutButton({ onSignOut }: { readonly onSignOut: () => void }) {
  return (
    <button
      type="button"
      onClick={onSignOut}
      className="inline-flex items-center text-sm font-medium text-[#0066FF] hover:underline"
    >
      <ArrowLeft className="h-4 w-4 mr-1" />
      Retour à la connexion
    </button>
  );
}

/** An account created but not approved yet. */
export function PendingApprovalCard({ onSignOut }: { readonly onSignOut: () => void }) {
  return (
    <Card tone="amber" icon={<Clock className="h-8 w-8" />} title="Compte en attente d'approbation">
      <p className="text-neutral-600 dark:text-neutral-400 mb-6">
        Votre compte a été créé mais n'a pas encore été approuvé par un administrateur.
      </p>
      <SignOutButton onSignOut={onSignOut} />
    </Card>
  );
}

/** An approved account that is not a member of the team: nothing of the company is open to it. */
export function NotAMemberCard({ onSignOut }: { readonly onSignOut: () => void }) {
  return (
    <Card tone="red" icon={<Shield className="h-8 w-8" />} title="Accès non autorisé">
      <p className="text-neutral-600 dark:text-neutral-400 mb-6">
        Votre compte ne fait pas partie de l'équipe de l'entreprise. Demandez une invitation à un administrateur.
      </p>
      <SignOutButton onSignOut={onSignOut} />
    </Card>
  );
}

/** The rights could not be read: say so, and offer to read them again. */
export function AccessErrorCard({ message, onRetry, onSignOut }: {
  readonly message: string;
  readonly onRetry: () => void;
  readonly onSignOut: () => void;
}) {
  return (
    <Card tone="red" icon={<Shield className="h-8 w-8" />} title="Impossible de vérifier vos accès">
      <p className="text-neutral-600 dark:text-neutral-400 mb-6">{message}</p>
      <div className="flex items-center justify-center gap-6">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center rounded-lg bg-[#0066FF] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#0052CC]"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Réessayer
        </button>
        <SignOutButton onSignOut={onSignOut} />
      </div>
    </Card>
  );
}

/** A screen of `/admin` that the person's roles do not open, shown inside the layout. */
export function ScreenClosedNotice({ home }: { readonly home: string | null }) {
  return (
    <div className="mx-auto mt-16 max-w-md rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-8 text-center">
      <Shield className="mx-auto mb-4 h-8 w-8 text-neutral-400" />
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Cette page ne vous est pas ouverte</h2>
      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
        Vos rôles dans l'équipe n'ouvrent pas cet écran. Un administrateur peut vous donner un rôle qui l'ouvre.
      </p>
      {home !== null && (
        <Link to={home} className="text-sm font-medium text-[#0066FF] hover:underline">
          Retour à l'accueil
        </Link>
      )}
    </div>
  );
}
